import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ProtectedRoute from './ProtectedRoute';
import { routeConstants } from '../constants/routeConstants';

import DashboardLayout from '../layouts/DashboardLayout';

// Authentication
import LoginPage from '../features/auth/pages/LoginPage';
import ForgotPasswordPage from '../features/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from '../features/auth/pages/ResetPasswordPage';
import RoleSelectionPage from '../features/auth/pages/RoleSelectionPage';

// Dashboard
import AdminDashboardPage from '../features/dashboard/pages/AdminDashboardPage';

// Students
import StudentListPage from '../features/students/pages/StudentListPage';
import AddStudentPage from '../features/students/pages/AddStudentPage';
import StudentDetailPage from '../features/students/pages/StudentDetailPage';

// Staff
import StaffListPage from '../features/staff/pages/StaffListPage';
import AddStaffPage from '../features/staff/pages/AddStaffPage';
import StaffViewPage from '../features/staff/pages/StaffViewPage';

// Fees
import FeeManagementPage from '../features/fees/pages/FeeManagementPage';
import FeeStructurePage from '../features/fees/pages/FeeStructurePage';
import FeeCollectionPage from '../features/fees/pages/FeeCollectionPage';

// Documents
import IDCardsPage from '../features/documents/pages/IDCardsPage';
import TemplatesPage from '../features/documents/pages/TemplatesPage';
import GenerationJobsPage from '../features/documents/pages/GenerationJobsPage';
import CardHistoryPage from '../features/documents/pages/CardHistoryPage';

// Academics
import TimetablePage from '../features/academics/pages/TimetablePage';

// Settings
import SettingsLayout from '../features/settings/pages/SettingsLayout';
import SchoolProfileSettingsPage from '../features/settings/pages/SchoolProfileSettingsPage';
import BrandingSettingsPage from '../features/settings/pages/BrandingSettingsPage';
import AcademicSettingsPage from '../features/settings/pages/AcademicSettingsPage';
import UserAccessSettingsPage from '../features/settings/pages/UserAccessSettingsPage';
import RolePermissionSettingsPage from '../features/settings/pages/RolePermissionSettingsPage';

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public authentication routes */}
      <Route path={routeConstants.LOGIN} element={<LoginPage />} />

      <Route
        path={routeConstants.FORGOT_PASSWORD}
        element={<ForgotPasswordPage />}
      />

      <Route
        path={routeConstants.RESET_PASSWORD}
        element={<ResetPasswordPage />}
      />

      {/* Role selection */}
      <Route
        path={routeConstants.ROLE_SELECTION}
        element={
          <ProtectedRoute>
            <RoleSelectionPage />
          </ProtectedRoute>
        }
      />

      {/* Protected dashboard routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Navigate
              to={routeConstants.DASHBOARD}
              replace
            />
          }
        />

        <Route
          path={routeConstants.DASHBOARD}
          element={<AdminDashboardPage />}
        />

        {/* Students */}
        <Route
          path={routeConstants.STUDENTS}
          element={<StudentListPage />}
        />

        <Route
          path={routeConstants.ADD_STUDENT}
          element={<AddStudentPage />}
        />

        <Route
          path={routeConstants.EDIT_STUDENT}
          element={<AddStudentPage />}
        />

        <Route
          path={routeConstants.STUDENT_DETAILS}
          element={<StudentDetailPage />}
        />

        {/* Staff */}
        <Route
          path={routeConstants.STAFF}
          element={<StaffListPage />}
        />

        <Route
          path={routeConstants.ADD_STAFF}
          element={<AddStaffPage />}
        />

        <Route
          path={routeConstants.EDIT_STAFF}
          element={<AddStaffPage />}
        />

        <Route
          path={routeConstants.STAFF_DETAILS}
          element={<StaffViewPage />}
        />

        {/* Fees */}
        <Route
          path={routeConstants.FEES_MANAGEMENT}
          element={<FeeManagementPage />}
        />

        <Route
          path={routeConstants.FEES_STRUCTURE}
          element={<FeeStructurePage />}
        />

        <Route
          path={routeConstants.FEES_COLLECTION}
          element={<FeeCollectionPage />}
        />

        <Route
          path="/fees"
          element={
            <Navigate
              to={routeConstants.FEES_MANAGEMENT}
              replace
            />
          }
        />

        {/* Documents */}
        <Route
          path={routeConstants.DOCUMENTS_ID_CARDS}
          element={<IDCardsPage />}
        />

        <Route
          path={routeConstants.DOCUMENTS_TEMPLATES}
          element={<TemplatesPage />}
        />

        <Route
          path={routeConstants.DOCUMENTS_JOBS}
          element={<GenerationJobsPage />}
        />

        <Route
          path={routeConstants.DOCUMENTS_HISTORY}
          element={<CardHistoryPage />}
        />

        <Route
          path="/documents"
          element={
            <Navigate
              to={routeConstants.DOCUMENTS_ID_CARDS}
              replace
            />
          }
        />

        {/* Academics */}
        <Route
          path={routeConstants.ACADEMICS_TIMETABLE}
          element={<TimetablePage />}
        />

        <Route
          path="/academics/*"
          element={
            <div className="flex h-full flex-col items-center justify-center text-gray-500">
              <h2 className="mb-2 text-2xl font-bold text-gray-900">
                Academics Module
              </h2>

              <p>
                These screens are currently under construction.
              </p>
            </div>
          }
        />

        {/* Settings */}
        <Route
          path={routeConstants.SETTINGS}
          element={<SettingsLayout />}
        >
          <Route
            index
            element={
              <Navigate
                to={routeConstants.SETTINGS_PROFILE}
                replace
              />
            }
          />

          <Route
            path="profile"
            element={<SchoolProfileSettingsPage />}
          />

          <Route
            path="branding"
            element={<BrandingSettingsPage />}
          />

          <Route
            path="academics"
            element={<AcademicSettingsPage />}
          />

          <Route
            path="users"
            element={<UserAccessSettingsPage />}
          />

          <Route
            path="roles"
            element={<RolePermissionSettingsPage />}
          />
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />
    </Routes>
  );
};

export default AppRoutes;